import { useState, useEffect, useRef } from 'react';
import { getSlidesForContentLibrary, replaceUnderscore } from './utils';

import ActionfigureElements from './ActionfigureElements';
import ContentBrowsing from './ContentBrowsing';

import constants from './constants.json';

import afLogo from '../../images/logos/af-no-text-logo.svg';
import contentLibrary from '../../images/icons/contentLibraryModal/content-library.svg';
import myFolderIcon from '../../images/icons/contentLibraryModal/person.svg';
import sharedFolderIcon from '../../images/icons/contentLibraryModal/people.svg';
import brandingFolderIcon from '../../images/icons/screens/paintbrush.svg';
import locationFolderIcon from '../../images/icons/contentLibraryModal/add-folder.svg';
import expandedIcon from '../../images/icons/menu/expanded.svg';
import collapsedIcon from '../../images/icons/menu/collapsed.svg';

const ICON_MAP = {
    my_folder: myFolderIcon,
    shared_folder: sharedFolderIcon,
    branding_folder: brandingFolderIcon
};

const ContentLibraryModal = ({ userData, currentCustomer, customerName, setIsOpenContentModal, contentModalData, permissions }) => {

    const [slides, setSlides] = useState({});
    const [expanded, setExpanded] = useState({});
    const [selected, setSelected] = useState(contentModalData?.page ? { main: contentModalData.page, sub: null } : { main: 'Actionfigure Elements', sub: null });
    const [editStatus, setEditStatus] = useState(contentModalData?.editType || null);
    // Add a refreshKey state to trigger re-fetch
    const [refreshKey, setRefreshKey] = useState(0);
     // Ref for modal content
    const modalRef = useRef(null);
    const selectedLocation = slides[selected.main]? slides[selected.main].find(obj => obj.locationName === selected.sub) : {};
    const [slideData, setSlideData] = useState(contentModalData?.slideData || null);

    // Close modal on outside click
    useEffect(() => {
        function handleClickOutside(event) {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                setIsOpenContentModal(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [setIsOpenContentModal]);

    useEffect(() => {
        async function fetchSlides() {
            if (currentCustomer && currentCustomer.id) {
                const slides = await getSlidesForContentLibrary({ id: currentCustomer.id }, userData.sub, userData.role === 'af_admin');
                setSlides(slides);
            }
        }
        fetchSlides();
    }, [currentCustomer, refreshKey]);

    // Callback to trigger refresh after save
    const handleSlideSaved = () => {
        setRefreshKey(prev => prev + 1);
    };

    // Separate keys by type
    let miscKeys = Object.keys(slides).filter(key => {
        if (!Array.isArray(slides[key]) || slides[key].length === 0) return false;
        const first = slides[key][0];
        // Exclude if first object has locationId (those are handled by locationKeys)
        return !first.locationId;
    });
    // Remove 'my_folder' if customer_id does not match... it means user is masked as another customer
    miscKeys = miscKeys.filter(key => {
        if (key === 'my_folder') {
            return userData.customer_id === currentCustomer.id;
        }
        return true;
    });

    // locationKeys is now an array of objects: { key, value }
    const locationKeys = Object.keys(slides)
    .filter(key =>
        Array.isArray(slides[key]) &&
        slides[key].length > 0 &&
        typeof slides[key][0] === 'object' &&
        slides[key][0].locationId
    )
    .map(key => ({ key, value: slides[key] }));

    const handleMainClick = (key) => {
        // Not a default item
        if (!constants.NAME_MAP[key]) {
            setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
        }
        else {
            selectNavItem(key, null)
        }
        setEditStatus(null);
        setSlideData(null);
    };

    const handleSubClick = (mainKey, subKey) => {
        selectNavItem(mainKey, subKey);
    };

    const getDisplayName = (key) => {
        return constants.NAME_MAP[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    }

    const selectNavItem = (main, sub) => {
        setEditStatus(null);
        setSelected({ main: main, sub: sub })
        setSlideData(null);
    }

    return (
        <div className="clm-modal-overlay">
            <div className="ContentLibraryModal" ref={modalRef}>
                <div className="clm-sidebar">
                    {/* Top icon */}
                    <div className="clm-title-container">
                        <img src={contentLibrary} alt="Content Library" style={{ width: '2.5em', height: '2.5em' }} />
                        <h4>Content library</h4>
                    </div>

                    {/* Actionfigure Elements section */}
                    <ul className="clm-sidebar-list">
                        <li>
                            <div
                                className={`clm-sidebar-item no-subkey${selected.main === 'Actionfigure Elements' ? ' selected' : ''}`}
                                onClick={() => selectNavItem('Actionfigure Elements', null)}
                            >
                                <img src={afLogo} alt="" className="clm-sidebar-item-icon" />
                                <div
                                    style={{ cursor: 'pointer' }}
                                >
                                    Actionfigure® Elements
                                </div>
                            </div>
                        </li>
                    </ul>
                    <hr style={{ margin: '1em 1em', border: 0, borderTop: '1px solid #e0e0e0' }} />
                    {/* Section 1: Misc tags */}
                    <ul className="clm-sidebar-list">
                        {miscKeys.map((key) => (
                            <li key={key}>
                                <div
                                    className={`clm-sidebar-item no-subkey${selected.main === key && !selected.sub ? ' selected' : ''}`}
                                    onClick={() => handleMainClick(key)}
                                >
                                    {ICON_MAP[key] && (
                                        <img src={ICON_MAP[key]} alt="" className="clm-sidebar-item-icon" />
                                    )}
                                    {getDisplayName(key)}
                                </div>
                            </li>
                        ))}
                    </ul>
                    {miscKeys.length > 0 && locationKeys.length > 0 && <hr style={{ margin: '1em 1em', border: 0, borderTop: '1px solid #e0e0e0' }} />}
                    {/* Section 2: Locations */}
                    <ul className="clm-sidebar-list">
                        {locationKeys.map((locObj) => (
                            <li key={locObj.key}>
                                <div
                                    className={`clm-sidebar-item${selected.main === locObj.key && !selected.sub ? ' selected' : ''}`}
                                    onClick={() => handleMainClick(locObj.key)}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                                >
                                    <span style={{ display: 'flex', alignItems: 'center' }}>
                                        <img src={locationFolderIcon} alt="" className="clm-sidebar-item-icon" />
                                        {replaceUnderscore(locObj.key, ', ')}
                                    </span>
                                    <img
                                        src={expanded[locObj.key] ? expandedIcon : collapsedIcon}
                                        alt={expanded[locObj.key] ? 'Expanded' : 'Collapsed'}
                                        style={{ width: '1em', height: '1em', marginLeft: '0.5em' }}
                                    />
                                </div>
                                {expanded[locObj.key] && (
                                    <ul className="clm-sidebar-sublist">
                                        {slides[locObj.key].map(subObj => (
                                            <li key={subObj.locationId}>
                                                <div
                                                    className={`clm-sidebar-subitem${selected.main === locObj.key && selected.sub === subObj.locationName ? ' selected' : ''}`}
                                                    onClick={() => handleSubClick(locObj.key, subObj.locationName)}
                                                >
                                                    {subObj.locationName}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="clm-content">
                    {
                        selected.main === 'Actionfigure Elements'
                        ? <ActionfigureElements customerName={customerName} setIsOpenContentModal={setIsOpenContentModal} selectCallback={contentModalData.selectCallback} />
                        : <ContentBrowsing
                            customerId={currentCustomer.id}
                            slides={selected.sub
                                ? selectedLocation?.slides
                                : slides[selected.main] ? slides[selected.main][0].slides : []
                            }
                            selected={selected}
                            selectedLocationId={selectedLocation?.locationId}
                            folderName={selected.sub || selected.main}
                            setIsOpenContentModal={setIsOpenContentModal}
                            editStatus={editStatus}
                            setEditStatus={setEditStatus}
                            onSlideSaved={handleSlideSaved}
                            setRefreshKey={setRefreshKey}
                            updatedBy={userData.sub}
                            role={userData.role}
                            slideData={slideData}
                            deviceType={contentModalData?.deviceType}
                            selectCallback={contentModalData.selectCallback}
                            permissions={permissions}
                            setSlideData={setSlideData}
                        />
                    }
                </div>
            </div>
        </div>
    );
};

export default ContentLibraryModal;
